// src/pages/DesignationManagement.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { DesignationForm } from '../components/DesignationForm';
import { DesignationList } from '../components/DesignationList';
import designationApi from '../api/designationApi';
import { Designation } from '../types/designation';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { Popup } from '../components/Popup';

export function DesignationManagement() {
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState<Designation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();

  useEffect(() => {
    fetchDesignations();
  }, []);

  const fetchDesignations = async () => {
    setLoading(true);
    try {
      const response = await designationApi.getDesignations();
      console.log('Full response:', response);
      
      // Handle different response structures
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else if (response && response.designations && Array.isArray(response.designations)) {
        data = response.designations;
      } else if (response && typeof response === 'object') {
        // If response is an object with data property
        data = response.data || [];
      } else {
        data = [];
      }
      
      console.log('Processed designations data:', data);
      setDesignations(data);
    } catch (err: any) {
      console.error('Failed to fetch designations:', err);
      showError('FETCH_ERROR', err.response?.data?.message || err.message || 'Failed to load designations');
      setDesignations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDesignation = async (data: any) => {
    console.log('Adding designation:', data);
    try {
      const response = await designationApi.addDesignation(data);
      console.log('Add response:', response);
      if (response.success !== false) {
        showSuccess('Success', 'Designation added successfully');
        await fetchDesignations();
        setIsFormOpen(false);
      } else {
        showError('ADD_ERROR', response.message || 'Failed to add designation');
      }
    } catch (err: any) {
      console.error('Failed to add designation:', err);
      showError('ADD_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to add designation');
    }
  };

  const handleUpdateDesignation = async (data: any) => {
    if (!editingDesignation?.id) return;
    
    // Prevent updating system designations
    if (editingDesignation.is_default === true || editingDesignation.created_by === 'system') {
      showError('SYSTEM_DESIGNATION', 'System default designations cannot be updated');
      return;
    }
    
    console.log('Updating designation:', editingDesignation.id, data);
    try {
      const response = await designationApi.updateDesignation(editingDesignation.id, data);
      console.log('Update response:', response);
      if (response.success !== false) {
        showSuccess('Success', 'Designation updated successfully');
        await fetchDesignations();
        setIsFormOpen(false);
        setEditingDesignation(null);
      } else {
        showError('UPDATE_ERROR', response.message || 'Failed to update designation');
      }
    } catch (err: any) {
      console.error('Failed to update designation:', err);
      showError('UPDATE_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to update designation');
    }
  };

  const handleDeleteDesignation = async (id: string) => {
    // Find the designation to check if it's a system designation
    const designationToDelete = designations.find(desig => desig.id === id);
    
    // Prevent deletion of system designations
    if (designationToDelete?.is_default === true || designationToDelete?.created_by === 'system') {
      showError('SYSTEM_DESIGNATION', 'System default designations cannot be deleted');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this designation? This action cannot be undone.')) {
      try {
        const response = await designationApi.deleteDesignation(id);
        console.log('Delete response:', response);
        if (response.success !== false) {
          showSuccess('Success', 'Designation deleted successfully');
          await fetchDesignations();
        } else {
          showError('DELETE_ERROR', response.message || 'Failed to delete designation');
        }
      } catch (err: any) {
        console.error('Failed to delete designation:', err);
        showError('DELETE_ERROR', err.response?.data?.detail?.[0]?.msg || err.message || 'Failed to delete designation');
      }
    }
  };

  const handleEditDesignation = (desig: Designation) => {
    // Prevent editing of system designations
    if (desig.is_default === true || desig.created_by === 'system') {
      showError('SYSTEM_DESIGNATION', 'System default designations cannot be edited');
      return;
    }
    setEditingDesignation(desig);
    setIsFormOpen(true);
  };

  const handleAddNewDesignation = () => {
    setEditingDesignation(null);
    setIsFormOpen(true);
  };

  // Safe filtering
  const filteredDesignations = Array.isArray(designations) ? designations.filter(desig =>
    desig?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    desig?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Count custom designations (non-system)
  const customDesignationsCount = Array.isArray(designations) ? designations.filter(desig => 
    desig?.is_default !== true && desig?.created_by !== 'system'
  ).length : 0;

  // Count system designations
  const systemDesignationsCount = Array.isArray(designations) ? designations.filter(desig => 
    desig?.is_default === true || desig?.created_by === 'system'
  ).length : 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Designation Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage job titles and designations</p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search designations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={handleAddNewDesignation}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Designation
        </button>
      </div>

      {/* Statistics Cards - Updated to show system vs custom */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total Designations</p>
          <p className="text-2xl font-bold text-blue-900">{Array.isArray(designations) ? designations.length : 0}</p>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">System Designations</p>
          <p className="text-2xl font-bold text-green-900">{systemDesignationsCount}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
          <p className="text-sm text-purple-600 font-medium">Custom Designations</p>
          <p className="text-2xl font-bold text-purple-900">{customDesignationsCount}</p>
        </div>
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
          <p className="text-sm text-orange-600 font-medium">Search Results</p>
          <p className="text-2xl font-bold text-orange-900">{filteredDesignations.length}</p>
        </div>
      </div>

      {/* Designation List */}
      <DesignationList
        designations={filteredDesignations}
        onEdit={handleEditDesignation}
        onDelete={handleDeleteDesignation}
        loading={loading}
      />

      {/* Designation Form Modal */}
      <DesignationForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingDesignation(null);
        }}
        onSubmit={editingDesignation ? handleUpdateDesignation : handleAddDesignation}
        initialData={editingDesignation}
        isLoading={loading}
        existingNames={(Array.isArray(designations) ? designations : [])
          .filter(d => d?.id !== editingDesignation?.id)
          .map(d => (d?.name || '').trim().toLowerCase())
          .filter(Boolean)}
      />

      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}